/**
 * WebM Video Exporter
 * Captures animated canvas frames and encodes them as WebM video
 * Supports audio tracks via Web Audio API and MediaRecorder
 */

// @ts-expect-error - webm-writer doesn't have types
import WebMWriter from 'webm-writer';
import type { AudioLayer } from '@/types/layers';
import { AudioMixer } from '@/lib/audio/mixer';

export interface WebmExportOptions {
  fps: number;
  quality: number; // 0.0 to 0.99999, higher is better (opposite of GIF)
  scale: number;
  duration: number; // Total duration in ms
  onProgress?: (progress: number) => void;
  audioLayers?: AudioLayer[]; // Optional audio tracks to include
}

const DEFAULT_OPTIONS: WebmExportOptions = {
  fps: 30,
  quality: 0.95,
  scale: 1,
  duration: 3000,
};

/**
 * Export canvas animation as WebM video
 *
 * @param canvasElement - The canvas element to capture
 * @param seekTo - Function to seek animation to a specific time (ms)
 * @param options - Export options
 * @returns Promise resolving to WebM blob
 */
export async function exportToWebM(
  canvasElement: HTMLElement,
  seekTo: (time: number) => void,
  options: Partial<WebmExportOptions> = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { fps, quality, scale, duration, onProgress, audioLayers } = opts;

  // If we have audio layers, use the MediaRecorder approach
  if (audioLayers && audioLayers.length > 0) {
    return exportToWebMWithAudio(canvasElement, seekTo, opts);
  }

  // Calculate frame count
  const frameCount = Math.ceil((duration / 1000) * fps);

  // Create WebM writer
  const videoWriter = new WebMWriter({
    quality,
    frameRate: fps,
    transparent: false,
  });

  // Dynamic import html2canvas
  const html2canvas = (await import('html2canvas')).default;

  // Capture frames
  for (let i = 0; i < frameCount; i++) {
    const time = (i / frameCount) * duration;

    // Seek to this frame's time
    seekTo(time);

    // Wait a tick for the DOM to update
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => setTimeout(resolve, 16)); // Extra delay for complex renders

    // Capture the frame
    const frameCanvas = await html2canvas(canvasElement, {
      scale,
      backgroundColor: null,
      logging: false,
      useCORS: true,
    });

    // Add frame to video
    videoWriter.addFrame(frameCanvas);

    // Report progress
    if (onProgress) {
      onProgress((i + 1) / frameCount * 0.9); // 90% for frame capture
    }
  }

  // Reset to start
  seekTo(0);

  // Complete the video and get blob
  if (onProgress) {
    onProgress(0.95); // Almost done
  }

  const blob = await videoWriter.complete();

  if (onProgress) {
    onProgress(1);
  }

  return blob;
}

/**
 * Export canvas animation with audio using MediaRecorder
 * This captures frames and audio in real-time
 */
async function exportToWebMWithAudio(
  canvasElement: HTMLElement,
  seekTo: (time: number) => void,
  options: WebmExportOptions
): Promise<Blob> {
  const { fps, quality, scale, duration, onProgress, audioLayers } = options;

  // Initialize audio mixer and load tracks
  const audioMixer = new AudioMixer();

  if (audioLayers) {
    for (const layer of audioLayers) {
      if (layer.visible) {
        await audioMixer.addTrackFromLayer(layer);
      }
    }
  }

  // Dynamic import html2canvas
  const html2canvas = (await import('html2canvas')).default;

  // Create an offscreen canvas for rendering
  const firstFrame = await html2canvas(canvasElement, {
    scale,
    backgroundColor: null,
    logging: false,
    useCORS: true,
  });

  const width = firstFrame.width;
  const height = firstFrame.height;

  // Create a canvas element for video capture
  const videoCanvas = document.createElement('canvas');
  videoCanvas.width = width;
  videoCanvas.height = height;
  const ctx = videoCanvas.getContext('2d')!;

  // Get video stream from canvas
  const videoStream = videoCanvas.captureStream(fps);

  // Get audio stream from mixer
  const audioStream = audioMixer.getMediaStream();

  // Combine video and audio streams
  const combinedTracks = [
    ...videoStream.getVideoTracks(),
    ...audioStream.getAudioTracks(),
  ];
  const combinedStream = new MediaStream(combinedTracks);

  // Set up MediaRecorder
  const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
    ? 'video/webm;codecs=vp9,opus'
    : 'video/webm';

  const mediaRecorder = new MediaRecorder(combinedStream, {
    mimeType,
    videoBitsPerSecond: Math.round(quality * 8000000), // Scale quality to bitrate
  });

  const chunks: Blob[] = [];
  mediaRecorder.ondataavailable = (e) => {
    if (e.data.size > 0) {
      chunks.push(e.data);
    }
  };

  // Start recording
  const recordingPromise = new Promise<Blob>((resolve) => {
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      resolve(blob);
    };
  });

  mediaRecorder.start(100); // Collect data every 100ms
  audioMixer.start(0);

  // Capture frames in real-time
  const frameCount = Math.ceil((duration / 1000) * fps);
  const frameInterval = 1000 / fps;

  for (let i = 0; i < frameCount; i++) {
    const time = (i / frameCount) * duration;

    // Seek animation to this frame's time
    seekTo(time);

    // Wait for the frame interval
    await new Promise(resolve => setTimeout(resolve, frameInterval));

    // Capture the frame
    const frameCanvas = await html2canvas(canvasElement, {
      scale,
      backgroundColor: null,
      logging: false,
      useCORS: true,
    });

    // Draw frame to video canvas
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(frameCanvas, 0, 0);

    // Report progress
    if (onProgress) {
      onProgress((i + 1) / frameCount * 0.9);
    }
  }

  // Stop recording
  audioMixer.stop();
  mediaRecorder.stop();

  // Reset to start
  seekTo(0);

  if (onProgress) {
    onProgress(0.95);
  }

  const blob = await recordingPromise;

  // Clean up
  audioMixer.dispose();
  videoStream.getTracks().forEach(t => t.stop());

  if (onProgress) {
    onProgress(1);
  }

  return blob;
}

/**
 * Download WebM blob as file
 */
export function downloadWebM(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.webm') ? filename : `${filename}.webm`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
